import React, { useState } from 'react';
import useXHR from '../../hooks/useXHR';
import { useAuth } from '../../contexts/AuthContext';

const ContentDumpModal = ({ isOpen, onClose, initialUrl = '', searchQuery = '' }) => {
  const { token } = useAuth();
  const { post } = useXHR(token);
  const [url, setUrl] = useState(initialUrl);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleDump = async () => {
    if (!url.trim()) { setError('Please enter a URL'); return; }
    setLoading(true);
    setError('');
    setResult(null);

    post('/content-dump', {
      body: { url: url.trim(), title: searchQuery || null },
      onSuccess: (data) => { setResult(data); setLoading(false); },
      onError: (err) => { setError(err || 'Failed to fetch content'); setLoading(false); }
    });
  };

  const handleCopy = () => {
    if (result?.full_text) {
      navigator.clipboard.writeText(result.full_text);
    }
  };

  return (
    <div data-testid="content-dump-modal" style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)'
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'linear-gradient(145deg, #FFFFFF, #FDFBF7)',
        borderRadius: 16, border: '1px solid rgba(252, 211, 77,0.3)',
        width: '90%', maxWidth: 900, maxHeight: '85vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid rgba(252, 211, 77,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <h3 data-testid="content-dump-title" style={{ color: '#334155', margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
              <i className="fas fa-file-alt" style={{ marginRight: 8, color: '#047857' }} />
              Web Content Dump
            </h3>
            {searchQuery && (
              <div style={{ color: '#047857', fontSize: '0.8rem', marginTop: 4 }}>
                Search: "{searchQuery}" - {new Date().toLocaleString()}
              </div>
            )}
          </div>
          <button data-testid="content-dump-close" onClick={onClose} style={{
            background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', fontSize: '1.2rem'
          }}>
            <i className="fas fa-times" />
          </button>
        </div>

        {/* URL Input */}
        <div style={{ padding: '16px 24px', display: 'flex', gap: 10 }}>
          <input
            data-testid="content-dump-url-input"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleDump()}
            placeholder="Enter URL to dump content from..."
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 8,
              background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(252, 211, 77,0.3)',
              color: '#334155', fontSize: '0.9rem', outline: 'none'
            }}
          />
          <button
            data-testid="content-dump-fetch-btn"
            onClick={handleDump}
            disabled={loading}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: loading ? '#4a3580' : 'linear-gradient(135deg, #FCD34D, #FDE68A)',
              color: '#0F172A', fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap'
            }}
          >
            {loading ? 'Fetching...' : 'Dump Content'}
          </button>
        </div>

        {error && (
          <div data-testid="content-dump-error" style={{ padding: '0 24px 12px', color: '#ef4444', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        {/* Results */}
        {result && (
          <div style={{ flex: 1, overflow: 'auto', padding: '0 24px 20px' }}>
            {/* Title & Meta */}
            <div style={{ marginBottom: 16, padding: 16, borderRadius: 10, background: 'rgba(252, 211, 77,0.1)', border: '1px solid rgba(252, 211, 77,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h4 data-testid="content-dump-result-title" style={{ color: '#334155', margin: '0 0 6px', fontSize: '1rem' }}>
                    {result.title || result.page_title || 'Untitled'}
                  </h4>
                  <div style={{ color: '#71717a', fontSize: '0.75rem' }}>
                    {result.timestamp && new Date(result.timestamp).toLocaleString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(16,185,129,0.15)', color: '#10b981', fontSize: '0.75rem' }}>
                    {result.word_count?.toLocaleString()} words
                  </span>
                  <span style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(245, 158, 11,0.15)', color: '#3b82f6', fontSize: '0.75rem' }}>
                    {result.char_count?.toLocaleString()} chars
                  </span>
                </div>
              </div>
              {result.meta_description && (
                <p style={{ color: '#a1a1aa', fontSize: '0.8rem', marginTop: 8 }}>{result.meta_description}</p>
              )}
              {result.meta_keywords && (
                <div style={{ color: '#047857', fontSize: '0.75rem', marginTop: 4 }}>Keywords: {result.meta_keywords}</div>
              )}
            </div>

            {/* Headings */}
            {result.headings?.length > 0 && (
              <details style={{ marginBottom: 12 }}>
                <summary style={{ color: '#047857', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                  Headings ({result.headings.length})
                </summary>
                <div style={{ marginTop: 6, paddingLeft: 12 }}>
                  {result.headings.map((h, i) => (
                    <div key={i} style={{ color: '#d4d4d8', fontSize: '0.8rem', paddingLeft: (h.level - 1) * 12, marginBottom: 2 }}>
                      <span style={{ color: '#71717a', marginRight: 6 }}>H{h.level}</span> {h.text}
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Full Text */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ color: '#047857', fontSize: '0.85rem', fontWeight: 600 }}>Full Text Content</span>
              <button data-testid="content-dump-copy-btn" onClick={handleCopy} style={{
                padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(252, 211, 77,0.3)',
                background: 'rgba(252, 211, 77,0.1)', color: '#047857', cursor: 'pointer', fontSize: '0.75rem'
              }}>
                <i className="fas fa-copy" style={{ marginRight: 4 }} /> Copy All
              </button>
            </div>
            <pre data-testid="content-dump-text" style={{
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              color: '#d4d4d8', fontSize: '0.8rem', lineHeight: 1.6,
              padding: 16, borderRadius: 10,
              background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(252, 211, 77,0.15)',
              maxHeight: 400, overflow: 'auto'
            }}>
              {result.full_text}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentDumpModal;
